/**
 * DocumentsScreen — the Ablage grid (dms/spec.md §M11, §4.1, §7). New sidebar
 * entry "Ablage": every document across Policen/Kunden/Schäden/Rechnungen/
 * Kommunikation, one row per document (ALL versions, not just latest — the
 * Client-Dokumente-tab/Policies-page "latest only" rule is M12's scope, not
 * this grid's).
 *
 * Columns exactly per spec (incl. the prototype-missing **State** column):
 * Dokument (clickable → the inline PDF viewer, `/ablage/$id`) · Kategorie ·
 * State · Bezug · Quelle · Datum · Grösse · Aktionen (single-row download /
 * set-state / delete, permission-gated).
 *
 * Filtering: `q` (fulltext — filename + `extracted_text_tsv`) is server-side,
 * debounced, via `useDocumentRows`. State/Kategorie/Quelle are CLIENT-SIDE
 * pills/dropdowns over that one broad fetch (`usePolicyRows` precedent) — see
 * `useDocumentRows.ts` for why `q` can't also cover `bezug` (the api's `q`
 * leg never touches it).
 *
 * Batch: DataTable's existing `selectable` machinery (the Aufgaben bulk-action
 * precedent) feeds a batch action bar → download (ZIP via
 * `POST /documents/batch/download`, single-selection uses the `/download`
 * attachment stream instead of a zip-of-one — DL-2 fix, wave dms-fixC) / delete (admin-only,
 * `document.delete`; the server skips still-active ids and reports them,
 * never fails the whole batch) / set-state (`document.set_state`).
 *
 * Volltext / Semantisch toggle (ea/fix-search — mirrors the Policies-page pattern,
 * `SegmentedControl` in `ListView`'s new `searchExtra` slot, directly beside the search
 * box): Volltext keeps its EXISTING behaviour unchanged (`useDocumentRows`' `q` param →
 * server-side fulltext over filename + `extracted_text_tsv`, already real content search —
 * unlike the old Policies-page bug, there was nothing broken here to fix). Semantisch is
 * `useDocumentSemanticSearch` → `GET /search?scope=document`.
 *
 * D2 fix: the toggle is a search-CRITERIA switch only; it must never swap the screen's
 * structure. BOTH modes render the SAME grid/tiles (the Policies-page precedent). In
 * Semantisch mode `useDocumentRows` fetches the broad, unfiltered window (no `q`) so the
 * semantic hits have a full row set to narrow + reorder (`searchFiltered`) instead of a
 * separate ranked hit-list layout replacing the table.
 *
 * [Source: apps/web/src/features/aufgaben/AufgabenScreen.tsx] (list chrome + bulk-bar precedent)
 * [Source: apps/web/src/features/policies/PoliciesScreen.tsx] (ListView + DataTable + client pills + Volltext/Semantisch toggle)
 * [Source: projects/broker-ag/dms/spec.md §4.1, §6, §M11]
 */
import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  Copy,
  Download,
  LayoutGrid,
  List,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { ListView } from '../../components/data/ListView';
import { DataTable } from '../../components/data/DataTable';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { EntityLink } from '../../components/ui/EntityLink';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { StatusDot } from '../../components/ui/StatusDot';
import { Toast } from '../../components/ui/Toast';
import { dateShortDe } from '../../lib/format';
import { saveBlob } from '../../lib/save-blob';
import { ApiError, apiClient } from '../../lib/api-client';
import { isLiveData } from '../../lib/data-mode';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { useDocumentRows } from './hooks/useDocumentRows';
import { useDocumentSemanticSearch } from './hooks/useDocumentSemanticSearch';
import { useDuplicateRows } from './hooks/useDuplicateRows';
import { SemanticDegradedNotice } from '../search/SemanticDegradedNotice';
import { categoryOptions, quelleBase, quelleOptions, sizeDisplay } from './api/documents-api';
import { UploadDocumentDialog } from './components/UploadDocumentDialog';
import { DuplicatesView } from './components/DuplicatesView';
import type { DocExtractionKind, DocumentListItemDto } from '@brokly/shared';

/**
 * State pills: 0 Alle, 1 Aktiv (default — dms/spec.md §4.1 "Default filter = active-only"),
 * 2 Inaktiv, 3 Duplikate — DMS Part 2 (P2-12): the admin-only "Duplikate" audit view,
 * ONLY appended to the pill list for admins (`useEffectivePermissions().isWildcard`,
 * mirroring the server's `GET /documents/duplicates` `@Roles('admin')` gate). A non-admin
 * never sees this pill or fires the duplicates fetch — the productive-stock default
 * (dedup_status='unique') is what every OTHER pill (Alle/Aktiv/Inaktiv) already shows,
 * since `GET /documents` excludes duplicates unless `include_duplicates=true` is sent.
 */
type StateView = 0 | 1 | 2 | 3;

/**
 * Volltext vs Semantisch (ea/fix-search): Volltext is the existing server-side fulltext
 * leg (`useDocumentRows`'s `q`, unchanged); Semantisch is `/search?scope=document`
 * (`useDocumentSemanticSearch`). D2 fix: both narrow/reorder the SAME grid; see the
 * `searchActive`/`searchFiltered` derivation below.
 */
type SearchMode = 'volltext' | 'semantisch';

/** P6: the batch rows of an inaktiv customer the api refused (write protection). */
const REFUSED_SUFFIX = 'schreibgeschützt (inaktiver Kunde)';

/** P6: a single write refused for an inaktiv customer shows the api's own sentence. */
function readOnlyMessage(err: unknown): string | null {
  return err instanceof ApiError && err.code === 'customer_read_only' ? err.message : null;
}

export function DocumentsScreen() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const live = isLiveData();
  const perms = useEffectivePermissions();

  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<SearchMode>('volltext');
  const [activeView, setActiveView] = useState<StateView>(1);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [quelleFilter, setQuelleFilter] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'tiles'>('table');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [feedback, setFeedback] = useState<string | null>(null);
  const [errorFeedback, setErrorFeedback] = useState<string | null>(null);
  // docs-fix item 3: a byte-identical upload dropped as a duplicate → red bottom-right toast.
  const [dedupToast, setDedupToast] = useState(false);

  const isDuplicatesView = activeView === 3;

  // D2 fix: Volltext keeps sending `search` server-side (its `q` leg is already real
  // content search, unchanged). Semantisch fetches the BROAD unfiltered window instead:
  // filtering happens locally via the semantic hits (`searchFiltered` below) so the SAME
  // grid/tiles render in both modes, never a separate hit-list layout.
  const { rows, totalCount, isLoading, isError } = useDocumentRows(
    mode === 'volltext' ? search : '',
  );

  const needle = search.trim();
  // Duplicates view wins regardless of mode; it's an unrelated admin audit table, not a
  // search result.
  const docSearch = useDocumentSemanticSearch(
    search,
    mode === 'semantisch' && !isDuplicatesView && needle.length > 0,
  );
  const documentHits = docSearch.hits.filter((h) => h.type === 'dokument');
  const searchActive = mode === 'semantisch' && !isDuplicatesView && docSearch.searched;
  // S4 follow-up: say it when the semantic leg is down, instead of passing keyword-only
  // hits off as the semantic answer. Only while Semantisch is actually the active mode.
  const semanticDegraded = searchActive && !docSearch.semanticAvailable;

  const hitRank = useMemo(() => {
    const map = new Map<string, number>();
    documentHits.forEach((hit, i) => map.set(hit.id, i));
    return map;
  }, [documentHits]);
  // DMS Part 2 (P2-12) — admin-only AND lazy: `enabled` requires BOTH
  // `perms.isWildcard` (a non-admin never fires this fetch — defense-in-depth
  // alongside the pill itself not being rendered below) AND the Duplikate pill
  // actually being active. This is deliberately NOT eager (`perms.isWildcard`
  // alone) — the productive Ablage view (Alle/Aktiv/Inaktiv) is where almost
  // every interaction happens, and firing this audit-only fetch on every mount
  // for every admin would be pure waste for a view most sessions never open.
  const {
    rows: duplicateRows,
    isLoading: duplicatesLoading,
    isError: duplicatesError,
  } = useDuplicateRows(perms.isWildcard && isDuplicatesView);

  function showFeedback(msg: string) {
    setFeedback(msg);
    setTimeout(() => setFeedback(null), 2500);
  }
  function showError(msg: string) {
    setErrorFeedback(msg);
    setTimeout(() => setErrorFeedback(null), 4000);
  }
  function showDedupToast() {
    setDedupToast(true);
    setTimeout(() => setDedupToast(false), 6000);
  }
  function invalidateList() {
    return qc.invalidateQueries({ queryKey: ['documents', 'list'] });
  }

  const stateFiltered = useMemo(
    () =>
      rows.filter((r) =>
        activeView === 0 ? true : activeView === 1 ? r.state === 'active' : r.state === 'inactive',
      ),
    [rows, activeView],
  );
  const categoryOpts = useMemo(() => categoryOptions(rows), [rows]);
  const quelleOpts = useMemo(() => quelleOptions(rows), [rows]);
  const visible = useMemo(
    () =>
      stateFiltered.filter(
        (r) =>
          (categoryFilter === '' || r.category === categoryFilter) &&
          (quelleFilter === '' || quelleBase(r.quelle) === quelleFilter),
      ),
    [stateFiltered, categoryFilter, quelleFilter],
  );

  // D2 fix: the rows actually rendered, same State/Kategorie/Quelle-filtered set as
  // Volltext, additionally narrowed + reordered by the semantic hits when Semantisch
  // has an active query. Never a different layout, just a different row selection.
  const searchFiltered = useMemo(() => {
    if (!searchActive) return visible;
    return visible
      .filter((r) => hitRank.has(r.id))
      .sort((a, b) => hitRank.get(a.id)! - hitRank.get(b.id)!);
  }, [visible, searchActive, hitRank]);

  // ── Selection (batch) ──
  function toggleRow(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAll(next: boolean) {
    setSelectedIds(next ? new Set(searchFiltered.map((r) => r.id)) : new Set());
  }
  function clearSelection() {
    setSelectedIds(new Set());
  }

  // ── Actions (single id array = one row's action; N ids = the batch bar) ──

  async function handleDownload(ids: string[]) {
    if (ids.length === 0) return;
    if (!live) {
      showFeedback(`${ids.length} Dokument${ids.length === 1 ? '' : 'e'} heruntergeladen`);
      clearSelection();
      return;
    }
    try {
      if (ids.length === 1) {
        const row = rows.find((r) => r.id === ids[0]);
        // DL-2 fix (wave dms-fixC): a genuine download goes through `document.download`
        // (`/download`, attachment), not `.content` (`document.read`, inline-viewer bytes).
        const blob = await apiClient.documents.download(ids[0]!);
        saveBlob(blob, row?.title ?? 'dokument.pdf');
      } else {
        const blob = await apiClient.documents.batchDownload(ids);
        saveBlob(blob, `dokumente-${new Date().toISOString().slice(0, 10)}.zip`);
      }
      showFeedback(`${ids.length} Dokument${ids.length === 1 ? '' : 'e'} heruntergeladen`);
    } catch {
      showError('Download fehlgeschlagen.');
    }
    clearSelection();
  }

  async function handleSetState(ids: string[], next: 'active' | 'inactive') {
    if (ids.length === 0) return;
    if (!live) {
      showFeedback(next === 'active' ? 'Als aktiv markiert' : 'Als inaktiv markiert');
      clearSelection();
      return;
    }
    try {
      if (ids.length === 1) {
        await apiClient.documents.setState(ids[0]!, next);
        await invalidateList();
        showFeedback(next === 'active' ? 'Als aktiv markiert' : 'Als inaktiv markiert');
      } else {
        const res = await apiClient.documents.batchSetState(ids, next);
        await invalidateList();
        const refused = res.refused?.length ?? 0;
        showFeedback(
          refused > 0
            ? `${res.updated.length} geändert, ${refused} ${REFUSED_SUFFIX}`
            : next === 'active' ? 'Als aktiv markiert' : 'Als inaktiv markiert',
        );
      }
    } catch (err) {
      showError(readOnlyMessage(err) ?? 'Status konnte nicht geändert werden.');
    }
    clearSelection();
  }

  async function handleDelete(ids: string[]) {
    if (ids.length === 0) return;
    const label = ids.length === 1 ? 'Dieses Dokument' : `${ids.length} Dokumente`;
    if (!window.confirm(`${label} endgültig löschen? Das kann nicht rückgängig gemacht werden.`)) {
      return;
    }
    if (!live) {
      showFeedback('Gelöscht');
      clearSelection();
      return;
    }
    try {
      if (ids.length === 1) {
        await apiClient.documents.delete(ids[0]!);
        await invalidateList();
        showFeedback('Dokument gelöscht');
      } else {
        const res = await apiClient.documents.batchDelete(ids);
        await invalidateList();
        const parts = [`${res.deleted.length} gelöscht`];
        if (res.skipped_active.length > 0) parts.push(`${res.skipped_active.length} noch aktiv (übersprungen)`);
        if ((res.refused?.length ?? 0) > 0) parts.push(`${res.refused.length} ${REFUSED_SUFFIX}`);
        showFeedback(
          parts.length > 1
            ? parts.join(', ')
            : `${res.deleted.length} Dokument${res.deleted.length === 1 ? '' : 'e'} gelöscht`,
        );
      }
    } catch (err) {
      showError(readOnlyMessage(err) ?? 'Löschen fehlgeschlagen.');
    }
    clearSelection();
  }

  async function handleUpload(file: File, opts: { docKind?: DocExtractionKind; title?: string }) {
    if (!live) {
      showFeedback('Dokument hochgeladen, wird analysiert …');
      return { document_id: 'mock-doc', job_id: 'mock-job', status: 'queued' };
    }
    const res = await apiClient.documents.upload(file, { filename: file.name, ...opts });
    await invalidateList();
    // docs-fix item 3: surface the Tier-1 dedup verdict. A byte-identical upload dropped as a
    // duplicate shows the red bottom-right toast (with a link to Admin → Duplikate for admins);
    // a genuine new upload shows the normal "wird analysiert" confirmation.
    if (res.dedup_status === 'duplicate') {
      showDedupToast();
    } else {
      showFeedback('Dokument hochgeladen, wird analysiert …');
    }
    return res;
  }

  const columns = useMemo<ColumnDef<DocumentListItemDto>[]>(
    () => [
      {
        accessorKey: 'title',
        header: 'Dokument',
        // Clickability rule (dms/spec.md §4.6): in the Ablage grid ONLY this
        // first column navigates — nothing else in a row is clickable.
        cell: ({ row }) => (
          <EntityLink
            onActivate={() => void navigate({ to: '/ablage/$id', params: { id: row.original.id } })}
            className="font-medium"
          >
            {row.original.title}
          </EntityLink>
        ),
      },
      {
        accessorKey: 'category',
        header: 'Kategorie',
        cell: ({ row }) =>
          row.original.category ? (
            <Badge variant="neutral">{row.original.category}</Badge>
          ) : (
            <span className="text-muted-foreground">-</span>
          ),
      },
      {
        accessorKey: 'state',
        header: 'State',
        cell: ({ row }) =>
          row.original.state === 'active' ? (
            <StatusDot tone="success" label="Aktiv" />
          ) : (
            <StatusDot tone="neutral" label="Inaktiv" />
          ),
      },
      {
        accessorKey: 'bezug',
        header: 'Bezug',
        cell: ({ row }) => (
          <span className="text-muted-foreground">{row.original.bezug ?? '-'}</span>
        ),
      },
      {
        accessorKey: 'quelle',
        header: 'Quelle',
        cell: ({ row }) => <span className="text-muted-foreground">{row.original.quelle}</span>,
      },
      {
        accessorKey: 'doc_date',
        header: 'Datum',
        cell: ({ row }) => (
          <span className="mono-num text-xs">{dateShortDe(row.original.doc_date)}</span>
        ),
      },
      {
        accessorKey: 'size_bytes',
        header: 'Grösse',
        cell: ({ row }) => (
          <div className="text-right mono-num">{sizeDisplay(row.original.size_bytes)}</div>
        ),
      },
      {
        id: 'actions',
        header: 'Aktionen',
        cell: ({ row }) => {
          const d = row.original;
          return (
            <div className="flex items-center justify-end gap-1">
              {perms.can('document.download') && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  aria-label={`${d.title} herunterladen`}
                  onClick={() => void handleDownload([d.id])}
                >
                  <Download className="size-3.5" />
                </Button>
              )}
              {perms.can('document.set_state') &&
                (d.state === 'active' ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    aria-label={`${d.title} als inaktiv markieren`}
                    onClick={() => void handleSetState([d.id], 'inactive')}
                  >
                    <Archive className="size-3.5" />
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    aria-label={`${d.title} als aktiv markieren`}
                    onClick={() => void handleSetState([d.id], 'active')}
                  >
                    <ArchiveRestore className="size-3.5" />
                  </Button>
                ))}
              {perms.can('document.delete') && d.state === 'inactive' && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 text-sem-alert hover:text-sem-alert"
                  aria-label={`${d.title} löschen`}
                  onClick={() => void handleDelete([d.id])}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [navigate, perms, rows, live],
  );

  const ids = [...selectedIds];

  return (
    <>
      <ListView
        title="Ablage"
        views={[
          { label: 'Alle', count: rows.length },
          { label: 'Aktiv', count: rows.filter((r) => r.state === 'active').length },
          { label: 'Inaktiv', count: rows.filter((r) => r.state === 'inactive').length },
          // DMS Part 2 (P2-12) — admin-only pill, only present in the array for admins
          // (`useEffectivePermissions().isWildcard`); a non-admin never sees it or can
          // navigate to `activeView === 3` at all. No `count` here (unlike the other
          // three pills) — the fetch is lazy (only fires once this pill is active, see
          // the `useDuplicateRows` call above), so a count shown up-front would either
          // be stale/wrong (0 before the first visit) or require an eager fetch this
          // audit-only view doesn't warrant; the live count shows in the toolbar once open.
          ...(perms.isWildcard ? [{ label: 'Duplikate' }] : []),
        ]}
        activeView={activeView}
        onViewChange={(i) => setActiveView(i as StateView)}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={
          mode === 'semantisch' ? 'Semantisch suchen …' : 'Titel, Dateiname, Inhalt …'
        }
        count={
          isDuplicatesView
            ? `${duplicateRows.length} Duplikat${duplicateRows.length === 1 ? '' : 'e'}`
            : searchActive
              ? `${searchFiltered.length} Treffer`
              : `${searchFiltered.length} von ${totalCount ?? rows.length} Einträgen`
        }
        searchNotice={semanticDegraded ? <SemanticDegradedNotice /> : undefined}
        searchExtra={
          // ea/fix-search: same toggle + placement pattern as PoliciesScreen — directly
          // beside the search box, not the top-right rightAction slot below (that one
          // holds page-level actions — Tabelle/Kacheln, upload — not search config).
          <SegmentedControl
            ariaLabel="Suchmodus"
            value={mode}
            onValueChange={(v) => setMode(v as SearchMode)}
            options={[
              { value: 'volltext', label: 'Volltext' },
              { value: 'semantisch', label: 'Semantisch' },
            ]}
          />
        }
        rightAction={
          isDuplicatesView ? undefined : (
            <div className="flex items-center gap-2">
              <SegmentedControl
                ariaLabel="Ansicht"
                value={viewMode}
                onValueChange={(v) => setViewMode(v as 'table' | 'tiles')}
                iconOnly
                options={[
                  { value: 'table', label: 'Tabelle', icon: <List className="size-4" /> },
                  { value: 'tiles', label: 'Kacheln', icon: <LayoutGrid className="size-4" /> },
                ]}
              />
              {perms.can('document.upload') && (
                <Button size="sm" onClick={() => setUploadOpen(true)}>
                  <Upload className="size-4" /> Dokument hochladen &amp; analysieren
                </Button>
              )}
            </div>
          )
        }
      >
        {isDuplicatesView ? (
          // DMS Part 2 (P2-12) — the admin "Duplikate" audit table: read-only, no
          // Kategorie/Quelle filters, no batch bar, no upload (none apply to an audit
          // trail). `enabled: perms.isWildcard` on the hook is the actual data gate;
          // this pill only existing in `views` for admins is the visibility gate.
          <DuplicatesView
            rows={duplicateRows}
            isLoading={duplicatesLoading}
            isError={duplicatesError}
          />
        ) : (
          <>
            {/* Kategorie / Quelle — client-side filters over the current fetch window. */}
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <select
                aria-label="Kategorie filtern"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground"
              >
                <option value="">Alle Kategorien</option>
                {categoryOpts.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <select
                aria-label="Quelle filtern"
                value={quelleFilter}
                onChange={(e) => setQuelleFilter(e.target.value)}
                className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground"
              >
                <option value="">Alle Quellen</option>
                {quelleOpts.map((q) => (
                  <option key={q} value={q}>
                    {q}
                  </option>
                ))}
              </select>
            </div>

            {/* Batch action bar — appears once ≥1 row is selected (Aufgaben bulk-bar precedent). */}
            {ids.length > 0 && (
              <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-secondary/50 px-3 py-2">
                <span className="text-sm font-medium text-foreground">{ids.length} ausgewählt</span>
                <div className="flex-1" />
                {perms.can('document.download') && (
                  <Button size="sm" variant="outline" onClick={() => void handleDownload(ids)}>
                    <Download className="size-4" /> Herunterladen
                  </Button>
                )}
                {perms.can('document.set_state') && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void handleSetState(ids, 'inactive')}
                    >
                      <Archive className="size-4" /> Als inaktiv markieren
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void handleSetState(ids, 'active')}
                    >
                      <ArchiveRestore className="size-4" /> Als aktiv markieren
                    </Button>
                  </>
                )}
                {perms.can('document.delete') && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-sem-alert hover:text-sem-alert"
                    onClick={() => void handleDelete(ids)}
                  >
                    <Trash2 className="size-4" /> Löschen
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={clearSelection}>
                  <X className="size-4" /> Auswahl aufheben
                </Button>
              </div>
            )}

            {isLoading ? (
              <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground">
                Lade Dokumente …
              </div>
            ) : isError ? (
              <div
                role="alert"
                className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground"
              >
                Dokumente konnten nicht geladen werden.
              </div>
            ) : searchActive && docSearch.isLoading ? (
              // D2 fix: while the debounced semantic search resolves, keep the SAME
              // grid/tiles shell (no layout swap), just a loading message in its place.
              <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground">
                Suche läuft …
              </div>
            ) : searchActive && searchFiltered.length === 0 ? (
              <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground">
                Keine Treffer für «{needle}».
              </div>
            ) : searchFiltered.length === 0 ? (
              <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground">
                Keine Dokumente in diesem Filter.
              </div>
            ) : viewMode === 'tiles' ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {searchFiltered.map((d) => (
                  <Card
                    key={d.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => void navigate({ to: '/ablage/$id', params: { id: d.id } })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter')
                        void navigate({ to: '/ablage/$id', params: { id: d.id } });
                    }}
                    className="cursor-pointer p-4 hover:border-primary/50"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-foreground">
                          {d.title}
                        </div>
                        <div className="mt-0.5 truncate text-xs text-muted-foreground">
                          {d.bezug ?? '-'}
                        </div>
                      </div>
                      {d.state === 'active' ? (
                        <StatusDot tone="success" label="Aktiv" />
                      ) : (
                        <StatusDot tone="neutral" label="Inaktiv" />
                      )}
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-1.5">
                      {d.category && <Badge variant="neutral">{d.category}</Badge>}
                      <span className="text-xs text-muted-foreground">{d.quelle}</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                      <span>{dateShortDe(d.doc_date)}</span>
                      <span className="mono-num">{sizeDisplay(d.size_bytes)}</span>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <DataTable
                columns={columns}
                data={searchFiltered}
                selectable
                getRowId={(r) => r.id}
                selectedIds={selectedIds}
                onToggleRow={toggleRow}
                onToggleAll={toggleAll}
              />
            )}
          </>
        )}
      </ListView>

      <UploadDocumentDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUpload={handleUpload}
        /* handleUpload owns the post-upload toast (dedup vs. wird-analysiert) — no toast here. */
      />

      {/* docs-fix item 3: dedup toast — red, bottom-right, with a link to Admin → Duplikate
          (admins only; the pill exists only for them). Checked FIRST so a duplicate upload
          shows this, not the green confirmation. */}
      {dedupToast ? (
        <Toast
          tone="alert"
          icon={<Copy className="size-4" />}
          onDismiss={() => setDedupToast(false)}
          action={
            perms.isWildcard
              ? {
                  label: 'Duplikate ansehen',
                  onClick: () => {
                    setActiveView(3);
                    setDedupToast(false);
                  },
                }
              : undefined
          }
        >
          Duplikat erkannt: Dokument bereits vorhanden
        </Toast>
      ) : feedback ? (
        <Toast tone="ok">{feedback}</Toast>
      ) : null}
      {errorFeedback && <Toast tone="alert">{errorFeedback}</Toast>}
    </>
  );
}
